/// REACT
import React, { useEffect, useState } from 'react';

//GRAPHQL
import { useQuery } from '@apollo/client';
import { getFragmentData } from '../../../__generated__';
import { PEOPLE_PAGE_QUERY } from '../../../features/graphql/query/person/personQueries';

/// MUI AND DIV COMPONENTS
import { GridColDef, GridNoRowsOverlay, GridRowModel } from '@mui/x-data-grid';
import { IconButton, Typography, Stack, Paper, Tooltip } from '@mui/material';
import { Close, Description } from '@mui/icons-material';
import { format } from 'date-fns';
import { TableFilterOption, TablePaginated, TableToolbarStacked } from '../../Manage/components';

///QUERIES, MUTATIONS, CONTEXT, HOOKS
import { PersonDataType } from '../../../context/SelectedPersonContext';
import { useSlideInContext } from '../../../context/SlideInContext';
import { ADD_USER } from '../../components/SlideIn/contentTypes';
import { SELECTED_PERSON_FRAGMENT } from '../../../features/graphql/query/person/fragments/peopleFragments';
import usePersonStore from '../../store/personStore';
import usePersonAuthStore, { UserRoleHierarchy } from "../../store/personAuthStore"
import { FetchMoreCallbackArgs } from '../../Manage/components/TablePaginated/TablePaginated';
const DEFAULT_PAGE_SIZE = 100;
///NOTE: THIS IS TO ORGANIZE THE RANKINGS AND ENSURE THAT PEOPLE CAN ONLY PUNCH DOWN, NOT UP

const TABLE_COLUMNS: GridColDef[] = [
    { field: "firstName", headerName: "First Name", flex: 1, sortable: true, },
    { field: "lastName", headerName: "Last Name", flex: 1, sortable: true },
    {
        field: "startDate", headerName: "Start Date", flex: 1, resizable: false, editable: false, width: 80,
        renderCell: ({ value }) => {
            return (<div> {value
                ? format(new Date(value.toString()), "MM/dd/yyyy")
                : ""}</div>)
        }
    },
    {
        field: "endDate", headerName: "End Date", flex: 1, resizable: false, editable: false, width: 80,
        renderCell: ({ value }) => {
            return (<div>
                {value
                    ? format(new Date(value.toString()), "MM/dd/yyyy")
                    : ""}
            </div>)
        }
    },

]
const mapEdgesToTable = (data: any) => {
    return {
        ...data.node,
        location: data.node
    }
}

type SelectedAction = "DETAILS" | "EXPIRE"

type StaffTableProps = {
    title: string,
    showNew: boolean
    onBack?: () => void
    onSelectedPerson: (record: any, action: SelectedAction) => void
}

export const StaffTable = ({ title, showNew, onBack, onSelectedPerson }: StaffTableProps) => {
    const { handleContent } = useSlideInContext();
    const [pageLoading, setPageLoading] = useState<boolean>(true);
    const defaultLocation = usePersonStore(state => state.company.defaultLocation);
    const myId = usePersonStore(state => state.id);
    const roles = usePersonAuthStore(state => state.roles)
    const myHighestRole = usePersonAuthStore((state) => state.highestRole)
    const { loading, data, fetchMore } = useQuery(PEOPLE_PAGE_QUERY, {
        notifyOnNetworkStatusChange: true,
        variables: {
            query: "",
            first: 100,
            after: null
        },
    });
    const [pageTableState, setPageTableState] = useState<{
        columns: GridColDef[];
        rows: GridRowModel[];
        filteredRows: GridRowModel[];
        status: "NORMAL" | "FILTERING";
    }>({
        columns: [],
        rows: [],
        filteredRows: [],
        status: "NORMAL"
    });

    useEffect(() => {
        setPageTableState((prevState) => {
            const columns = [...TABLE_COLUMNS];
            columns.push({
                field: "roleType", headerName: "Role / Type", flex: 1, sortable: true,
                renderCell: (params) => {
                    const roleType = params.row.role?.name
                    return <div>{roleType}</div>
                }
            },)
            columns.push({
                field: 'details',
                headerAlign: "center",
                align: "center",
                headerName: 'Details',
                sortable: false,
                resizable: false,
                editable: false,
                width: 80,
                renderCell: (params) => {
                    const record = data?.people?.edges?.find((edge) => {
                        const fragment = getFragmentData(SELECTED_PERSON_FRAGMENT, edge.node);
                        return fragment.id == params.row.id
                    })
                    return (
                        <IconButton onClick={() => onSelectedPerson(record?.node as PersonDataType, "DETAILS")}>
                            <Description />
                        </IconButton>
                    );
                },
            });
            if (!roles.includes("SUPPORT ADMIN")) {
                columns.push({
                    align: "center",
                    headerAlign: "center",
                    sortable: false,
                    resizable: false,
                    editable: false,
                    disableColumnMenu: true,
                    field: "id",
                    width: 80,
                    headerName: "Expire",
                    renderCell: (params) => {
                        const status = params.row.expired
                        const isSelf = params.row.id === myId;
                        const role = params.row.role.normalizedName
                        const userDoesNotHaveAccess = UserRoleHierarchy[myHighestRole as keyof typeof UserRoleHierarchy] < UserRoleHierarchy[role as keyof typeof UserRoleHierarchy]
                        let disabledMessage;
                        if (isSelf) {
                            disabledMessage = "Cannot Archive Self"
                        } else if (status) {
                            disabledMessage = "User already archived"
                        } else if (userDoesNotHaveAccess) {
                            disabledMessage = "Cannot archive user with more permissions"
                        }
                        else {
                            disabledMessage = ""
                        }
                        const record = data?.people?.edges?.find((edge) => {
                            const fragment = getFragmentData(SELECTED_PERSON_FRAGMENT, edge.node);
                            return fragment.id == params.row.id
                        })
                        return (
                            <Tooltip title={disabledMessage}>
                                <div>
                                    <IconButton onClick={() => onSelectedPerson(record?.node as PersonDataType, "EXPIRE")}>
                                        <Close
                                            style={{
                                                backgroundColor: status || isSelf || userDoesNotHaveAccess ? "#EEE" : "#8E1C3B",
                                                borderRadius: "100%",
                                                color: status || isSelf || userDoesNotHaveAccess ? "#999" : "#FFF",
                                            }}
                                        />
                                    </IconButton>
                                </div>
                            </Tooltip>
                        )
                    }
                })
            }
            if (data) {
                setPageLoading(false)
            }
            const rows = data?.people?.edges?.map(mapEdgesToTable) ?? [];
            return {
                ...prevState,
                columns,
                filteredRows: [],
                rows
            }
        });
    }, [loading, data])

    const handleFilter = (selectedOption: TableFilterOption) => {
        setPageTableState((prevState) => {
            const filteredRows = selectedOption === "ACTIVE"
                ? prevState.rows.filter((x) => !x.expired)
                : selectedOption == "EXPIRED"
                    ? prevState.rows.filter((x) => x.expired)
                    : prevState.rows; // Default "ALL"

            return {
                ...prevState,
                status: selectedOption === "ALL" ? "NORMAL" : "FILTERING",
                filteredRows,
            };
        });
    }
    useEffect(() => {
        console.log("the loading is: ", loading)
    }, [loading])
    const handleFetchMore = async (variables: FetchMoreCallbackArgs) => {
        await fetchMore({
            variables: {
                query: variables.query ?? "",
                after: variables.after ?? null,
                first: variables.first ?? 100
            }
        }).then((res) => {
            const tempRows = res?.data?.people?.edges?.map(mapEdgesToTable)
            if (tempRows && tempRows.length > 0) {
                setPageTableState(prev => ({
                    ...prev,
                    rows: tempRows
                }))
            }
        }).catch(err => {
            console.log("there is an error: ", err)
        })
    }
    const handleOnNew = () => {
        handleContent(ADD_USER)
    }
    const totalRows = pageTableState.status === "FILTERING"
        ? pageTableState.filteredRows.length
        : data?.people?.totalCount ?? 0;

    return (
        <Paper>
            <TablePaginated
                loading={loading || pageLoading}
                renderToolbar={({ searchQuery, onSearch, onSearchClear }) => (
                    <TableToolbarStacked
                        title={title}
                        subtitle={`@ ${defaultLocation?.name}`}
                        newButtonLabel={showNew ? 'Add new User' : ""}
                        searchQuery={searchQuery ?? ""}
                        onNew={showNew ? handleOnNew : undefined}
                        onBack={onBack}
                        onFilter={handleFilter}
                        onSearch={onSearch}
                        onSearchClear={onSearchClear} />
                )}
                slots={{
                    noRowsOverlay: (data?.location?.totalStaff ?? 0) <= 0
                        ? NoStaffFoundOverlay
                        : GridNoRowsOverlay
                }}
                pageInfo={data?.people?.pageInfo}
                pageSize={DEFAULT_PAGE_SIZE}
                columns={pageTableState.columns}
                rows={pageTableState.status === "FILTERING"
                    ? pageTableState.filteredRows
                    : pageLoading ? [] : pageTableState.rows}
                checkboxSelection={false}
                getRowClassName={(params) => params.row.expired ? "Expired" : "Active"}
                totalRows={totalRows}
                onFetchMore={handleFetchMore}
            />
        </Paper>
    )
}



function NoStaffFoundOverlay() {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center">
            <Typography width={500}>
                You do not have any staff associated with this location. Select Add bew Staff Member to get started or contact your futuresTHRIVE representative.
            </Typography>
        </Stack>
    )
}